import {
  swrArgAdapter,
  updaterToFetch,
  useFetcher,
  useMockableFetcher,
  useMockableRawUpdater,
  useRawFetcher,
} from "@sanmanuel/swr";
import { mockPlayerDataStore } from "services/mock-api/player-data";
import {
  IBasicReservation,
  IPlayerTrip,
  IPlayerComp,
  IBasicPlayerNote,
  IPlayerNote,
  INoteTopic,
  ICreateNote,
  IEditNote,
  IPlayerCreditTransaction,
  IPlayerOffer,
  IOfferCategory,
  IBasicPlayerInformation,
  IPlayerLanguage,
  IUpdatePlayerLanguage,
  IPlayerInformation,
  IPlayerCreditMarkers,
  IPlayerSearchInfo,
  INetRevenueAnalysis,
  IPlayerStatsForTimePeriod,
  IPlayerTripHistory,
  ITimePeriod,
  IPlayerTierInfo,
} from "services/models";
import {
  HostFavoritePlayer,
  IHostActivePlayer,
  IHostEvent,
  IHostPlayer,
  IHostPlayerTierSumamry,
  IHostPromotion,
  IHostPromotionDetail,
  IHostReservation,
  IHostTrendingCustomerScore,
  IHostTrip,
  IUnHostedActivePlayer,
} from "services/models/Host";
import { IHostPlayerAudit } from "services/models/Host/HostGuestAudit";
import { IPlayerCustomerValueScore } from "services/models/Player/PlayerCustomerValueScore";
import useSWR, { SWRResponse } from "swr";
import { ApiService } from "./ApiService";
import { delay } from "@sanmanuel/core";
import { IUpdateSourceOfFundsInfo } from "components/Player/PlayerOverviewInformationCard";
import { IPlayerCreditAccount } from "services/models/Player/PlayerCreditAccount";
import { mockHostDataStore } from "services/mock-api/host-data";
import { mockPeriods, mockTripsForPeriods } from "services/mock-api/data";
import {
  IBuildUpPlayer,
  IBuildUpStatus,
  IBuildUpStatusUpdate,
  IFreePlayStats,
  ICurrentBuildUpStatus,
} from "services/models/BuildUpPlayer";
import { IClubSerranoInfo, IPlayerMobileAppInformation } from "services/models/Player/PlayerMobileAppInformation";
import { mockReservations } from "services/mock-api/reservations-data";
import { useCallback, useMemo } from "react";

const swrOptions = { dedupingInterval: 15 * 60 * 1000 };
const successProbability = 0.9;

const useInfiniteLoadingFetcher = <T>() => {
  const fetcher = useFetcher<T>();
  return (r: any) => delay(() => fetcher(r), 1000000);
};

export const usePlayerSearch = (term: string | undefined) => {
  const mockableSearch = useMockableFetcher<IPlayerSearchInfo[]>(async (_, init) => {
    const body = JSON.parse((init?.body as string) ?? "{}");
    return mockPlayerDataStore.getPlayers(body);
  });
  const request = updaterToFetch("POST", `${ApiService.v1Url}/players/search`, { term: term });
  return useSWR<IPlayerSearchInfo[]>(term ? request : null, swrArgAdapter(mockableSearch));
};

/**
 * Player's Club Info
 * @param {string | number} playerId - ID of player
 * @returns {SWRResponse<IClubSerranoInfo>} SWRResponse of player's club serrano info
 */
export const useClubInfo = (playerId: string | number) => {
  return useSWR<IClubSerranoInfo>(`${ApiService.v1Url}/players/${playerId}/club-serrano-online/info`);
};

/**
 * Player's Mobile App Info
 * @param {string | number} playerId - ID of player
 * @returns {SWRResponse<IPlayerMobileAppInformation>} SWRResponse of player's mobile app info
 */
export const useMobileAppInfo = (playerId: string | number) => {
  return useSWR<IPlayerMobileAppInformation>(`${ApiService.v1Url}/players/${playerId}/app/info`);
};

/**
 * Player's Tier Info
 * @param {string | number} playerId - ID of player
 * @returns {SWRResponse<IPlayerTierInfo>} SWRResponse of player's tier info
 */
export const usePlayerTierInfo = (playerId: string | number) => {
  return useSWR<IPlayerTierInfo>(`${ApiService.v1Url}/players/${playerId}/tierinfo`);
};

/**
 * Player Reservations
 * @param {'active' | 'upcoming' } status - Status of Player Reservation
 * @returns {SWRResponse<IHostReservation[]>} - SWRResponse of reservations for the player
 */
export const useHostPlayerReservations = (status: "active" | "upcoming"): SWRResponse<IHostReservation[]> => {
  return useSWR<IHostReservation[]>(`${ApiService.v1Url}/host/players/reservations/${status}`, swrOptions);
};

/**
 * Player Reservations Cancellation
 * @returns {(playerId: string | number, confirmationNumber: string | number) => {}: Promise<Response>}} - returns a callback that returns a Response object
 */
export const useCancelReservation = () => {
  const mockableCancel = useMockableRawUpdater((_: "PUT" | "POST" | "DELETE", requestInfo: RequestInfo, body) =>
    delay(() => {
      if (Math.random() > successProbability) {
        return Promise.reject(new Error(`Cancel reservation call failed`));
      }

      return Promise.resolve(new Response(JSON.stringify("Reservation cancelled"), { status: 200 }));
    })
  );

  return useCallback(
    (playerId: string | number, confirmationNumber: string | number): Promise<Response> =>
      mockableCancel(
        "DELETE",
        `${ApiService.v1Url}/players/${playerId}/reservations/${confirmationNumber}/cancel`,
        undefined
      ),
    [mockableCancel]
  );
};

/**
 * Player Trips
 * @param {string | number} playerId - ID of player
 * @returns {SWRResponse<IPlayerTrip[]>} SWRResponse with records of previous trips for the player
 */

export const usePlayerTrips = (playerId: string | number): SWRResponse<IPlayerTrip[]> => {
  return useSWR<IPlayerTrip[]>(`${ApiService.v1Url}/players/${playerId}/trips`);
};

/**
 * Player Comps
 * @param {string | number} playerId - ID of player
 * @returns {SWRResponse<IPlayerComp[]>} SWRResponse with records of comps for the player
 */

export const usePlayerComps = (playerId: string | number): SWRResponse<IPlayerComp[]> => {
  return useSWR<IPlayerComp[]>(`${ApiService.v1Url}/players/${playerId}/comps`);
};

/**
 * Player Credit Transactions
 * @param {string | number} playerId - ID of player
 * @returns {SWRResponse<IPlayerCreditTransaction[]>} Records of credit transaction
 */
export const usePlayerCreditTransactions = (playerId: string | number): SWRResponse<IPlayerCreditTransaction[]> => {
  return useSWR<IPlayerCreditTransaction[]>(`${ApiService.v1Url}/players/${playerId}/credit`);
};

export const usePlayerCreditMarkers = (playerId: string | number): SWRResponse<IPlayerCreditMarkers> => {
  return useSWR<IPlayerCreditMarkers>(`${ApiService.v1Url}/players/${playerId}/credit/markers`);
};

export const usePlayerCreditAccount = (playerId: string | number): SWRResponse<IPlayerCreditAccount> => {
  return useSWR<IPlayerCreditAccount>(`${ApiService.v1Url}/players/${playerId}/credit/account`);
};

/**
 * Player Notes
 * @param {string | number} playerId - ID of player
 * @returns {SWRResponse<IBasicPlayerNote[]>} SWRResponse with records of notes for the player
 */
export const usePlayerNotes = (playerId: string | number): SWRResponse<IBasicPlayerNote[]> => {
  const mockableFetcher = useMockableFetcher<IBasicPlayerNote[]>((r: any) => {
    const notes = mockPlayerDataStore.notes;
    return Promise.resolve(notes);
  });
  return useSWR<IBasicPlayerNote[]>(`${ApiService.v1Url}/players/${playerId}/notes`, mockableFetcher);
};

/**
 * Note details
 * @param {number} noteId - ID of note
 * @returns {SWRResponse<IPlayerNote>} SWRResponse with details for a specific note
 */
export const usePlayerNote = (noteId: number): SWRResponse<IPlayerNote> => {
  const mockableFetcher = useMockableFetcher<IPlayerNote>((r: RequestInfo) => {
    const note = mockPlayerDataStore.getNote(noteId);
    return Promise.resolve(note);
  });
  return useSWR<IPlayerNote>(`${ApiService.v1Url}/notes/${noteId}`, mockableFetcher, { revalidateOnMount: true });
};

/**
 * Note Topics
 * @returns {SWRResponse<INoteTopic[]>} SWRResponse with list of note topics
 */
export const useNoteTopic = (): SWRResponse<INoteTopic[]> => {
  const mockableFetcher = useMockableFetcher<INoteTopic[]>((r: RequestInfo) => {
    const topics = mockPlayerDataStore.topics;
    return Promise.resolve(topics);
  });
  return useSWR<INoteTopic[]>(`${ApiService.v1Url}/topics`, mockableFetcher, { revalidateOnMount: true });
};

/**
 * Add a new note
 * @returns {{ createNote: (note: ICreateNote): Promise<Response>}} Object that contains editNote function that takes in an 'ICreateNote' object and returns a promise of a response
 */

export const useAddNote = (): { createNote: (note: ICreateNote) => Promise<Response> } => {
  const mockableUpdate = useMockableRawUpdater<ICreateNote>(
    (_: "PUT" | "POST" | "DELETE", requestInfo: RequestInfo, body) =>
      delay(() => {
        const newNote = mockPlayerDataStore.addNote(body);
        // return Promise.reject(new Response("create note err", { status: 500 }));

        return Promise.resolve(new Response(JSON.stringify(newNote), { status: 200 }));
      })
  );
  return {
    createNote: (note: ICreateNote) => {
      return mockableUpdate("POST", `${ApiService.v1Url}/notes`, note, {
        headers: ApiService.playerNotesHeader,
      });
    },
  };
};

/**
 * Updates existing note
 * @returns {{ editNote: (note: IEditNote): Promise<Response>}} Object that contains editNote function that takes in an 'IEditNote' object and returns a promise of a response
 */
export const useEditNote = (): { editNote: (note: IEditNote) => Promise<Response> } => {
  const mockableUpdate = useMockableRawUpdater<IEditNote>(
    (_: "PUT" | "POST" | "DELETE", requestInfo: RequestInfo, body) => {
      const newNote = mockPlayerDataStore.editNote(body);
      return Promise.resolve(new Response(JSON.stringify(newNote), { status: 200 }));
    }
  );
  return {
    editNote: (note: IEditNote) => {
      return mockableUpdate("PUT", `${ApiService.v1Url}/notes`, note, {
        headers: ApiService.playerNotesHeader,
      });
    },
  };
};

export const useHostActivePlayerSummary = () => {
  return useSWR<IHostPlayerTierSumamry>(`${ApiService.v1Url}/host/players/tiersummary`);
};

/**
 * Host's Trending Customer Values
 * @returns {SWRResponse<IHostTrendingCustomerScore[]>} SWRResponse of host trending customers' score
 */
export const useHostTrendingCustomerScores = (): SWRResponse<IHostTrendingCustomerScore[]> => {
  return useSWR<IHostTrendingCustomerScore[]>(`${ApiService.v1Url}/host/players/customerscore/`, swrOptions);
};

/**
 * Host's Player Trip Trend
 * @param {'increasing' | 'decreasing' } type - Trip's type
 * @returns {SWRResponse<IHostTrip[]>} - An SWRResponse of records of increasing/decreasing trips from the player
 */
export const useHostPlayerTrips = (type: "increasing" | "decreasing"): SWRResponse<IHostTrip[]> => {
  return useSWR<IHostTrip[]>(`${ApiService.v1Url}/host/players/trips/${type}`, swrOptions);
};

/**
 * Upcoming Player Birthdays
 * @param {string | number | null} days - Amount of days tll the next upcoming birthdays
 * @returns {SWRResponse<IHostActivePlayer[]>} - SWRResponse of players with upcoming birthdays
 */
export const useHostPlayersBirthday = (days?: string | number | null) => {
  const url = `${ApiService.v1Url}/host/players/birthdays${days ? "?inNextDays=" + days : ""}`;
  return useSWR<IHostPlayer[]>(url, swrOptions);
};

/**
 * Host Promotions
 * @returns {SWRResponse<IHostPromotion[]>} - SWRResponse of host promotions
 */
export const useHostPromotions = (): SWRResponse<IHostPromotion[]> => {
  return useSWR<IHostPromotion[]>(`${ApiService.v1Url}/host/players/calendar/promotions`);
};

/**
 * Player Invite Only Promotions & Events
 * @returns {SWRResponse<IHostPromotion[]>} - SWRResponse of player promotions
 */
export const usePlayerPromotionsAndEvents = (playerId: string): SWRResponse<IHostPromotion[]> => {
  const mockableFetcher = useMockableFetcher<IHostPromotion[]>((r: any) =>
    delay(() => {
      const promotions = mockHostDataStore.promotions;
      console.log("Mocking", r, promotions);

      if (Math.random() > successProbability) {
        throw new Error(`Get player promotions call failed`);
      }

      return promotions;
    })
  );

  return useSWR<IHostPromotion[]>(`${ApiService.v1Url}/calendar/${playerId}`, mockableFetcher);
};

/**
 * Player Promotion Details
 * @returns {SWRResponse<IHostPromotionDetail>} - SWRResponse of player promotion details
 */
export const useGetPlayerPromotionsDetails = (playerId: string): SWRResponse<IHostPromotionDetail> => {
  // `${ApiService.v1Url}/calendar/promotions/${id}`;
  return useSWR<IHostPromotionDetail>(`${ApiService.v1Url}/calendar/promotions/${playerId}`);
};

/**
 * Host Events
 * @returns {SWRResponse<IHostEvent[]>} - SWRResponse of host events
 */
export const useHostEvents = (): SWRResponse<IHostEvent[]> => {
  return useSWR<IHostEvent[]>(`${ApiService.v1Url}/host/players/calendar/events`);
};

/**
 * Guest and Host Audit
 * @returns {SWRResponse<IHostPlayerAudit[]>} - SWRResponse of host player audit records
 */
export const useHostPlayersAudit = (): SWRResponse<IHostPlayerAudit[]> => {
  return useSWR<IHostPlayerAudit[]>(`${ApiService.v1Url}/host/audit/players`, swrOptions);
};

/**
 * Host's Active Players
 * @returns {SWRResponse<IHostActivePlayer[]>} SWRResponse host's active players
 */
export const useHostActivePlayersOnFloor = (): SWRResponse<IHostActivePlayer[]> => {
  const mockableFetcher = useMockableFetcher<IHostActivePlayer[]>((r: any) =>
    delay(() => {
      const activePlayers = mockHostDataStore.activePlayers;
      console.log("Mocking", r, activePlayers);

      if (Math.random() > successProbability) {
        throw new Error(`Get host active players call failed`);
      }

      return activePlayers;
    })
  );

  return useSWR<IHostActivePlayer[]>(`${ApiService.v1Url}/host/players/active`, mockableFetcher);
};

/**
 * Host's Favorite Players
 * @returns {SWRResponse<HostFavoritePlayer[]>} SWRResponse host's favorite players
 */
export const useHostFavoritePlayer = (): SWRResponse<HostFavoritePlayer[]> => {
  const mockableFetcher = useMockableFetcher<HostFavoritePlayer[]>((r: any) =>
    delay(() => {
      const favoritePlayers = mockHostDataStore.favoritePlayers;
      console.log("Mocking", r, favoritePlayers);

      if (Math.random() > successProbability) {
        throw new Error(`Get host favorite players call failed`);
      }

      return favoritePlayers;
    })
  );

  return useSWR<HostFavoritePlayer[]>(`${ApiService.v1Url}/host/players/favorites`, mockableFetcher);
};

/**
 * Favorite player updater
 * @returns {follow: (playerId: string): Promise<Response>,  unfollow: (playerId: string): Promise<Response> => Promise<Response>}
 * - Object that contains follow and unfollow method that takes in playerId
 * as params and returns a promise of an api response to follow/unfollow specified player
 */
export const useFavoritesUpdater = () => {
  const mockableFollow = useMockableRawUpdater((_, req, body) => {
    const playerId = parseInt((req as string).split("/").reverse()[0]);

    if (Math.random() > successProbability) {
      mockHostDataStore.followPlayer(playerId);
      return Promise.reject(new Error(`Follow player call failed`));
    }

    return Promise.resolve(new Response());
  });

  const mockableUnfollow = useMockableRawUpdater((_, req, body) => {
    const playerId = parseInt((req as string).split("/").reverse()[0]);

    if (Math.random() > successProbability) {
      mockHostDataStore.unfollowPlayer(playerId);
      const faves = mockHostDataStore.favoritePlayers;
      console.log("faves", faves);
      return Promise.reject(new Error(`Unfollow player call failed`));
    }

    return Promise.resolve(new Response());
  });

  return useMemo(() => {
    return {
      follow: (playerId: string | number): Promise<Response> => {
        return mockableFollow("POST", `${ApiService.v1Url}/host/players/favorites/${playerId}`, null);
      },
      unfollow: (playerId: string | number): Promise<Response> => {
        return mockableUnfollow("DELETE", `${ApiService.v1Url}/host/players/favorites/${playerId}`, null);
      },
    };
  }, [mockableFollow, mockableUnfollow]);
};

/**
 * Hot Players on the Floor
 * @returns {SWRResponse<IUnHostedActivePlayer[]>} SWRResponse of hot players on the floor
 */
export const useUnHostedActivePlayersOnFloor = (): SWRResponse<IUnHostedActivePlayer[]> => {
  return useSWR<IUnHostedActivePlayer[]>(`${ApiService.v1Url}/host/players/active/unhosted`);
};

/**
 * Build up players on the Floor
 * @returns {SWRResponse<BuildUpPlayers[]>} SWRResponse of build up players on the floor
 */
export const useBuildUpPlayersOnFloor = (): SWRResponse<IBuildUpPlayer[]> => {
  const mockableFetcher = useMockableFetcher<IBuildUpPlayer[]>((r: any) =>
    delay(() => {
      const buildUpPlayers = mockPlayerDataStore.getBuildUpPlayers();
      console.log("Mocking", r, buildUpPlayers);

      if (Math.random() > successProbability) {
        throw new Error(`Get build up players call failed`);
      }

      return buildUpPlayers;
    })
  );

  return useSWR<IBuildUpPlayer[]>(`${ApiService.v1Url}/host/players/antifraud/current`, mockableFetcher);
};

/**
 * All available Build up players' statuses
 * @returns {SWRResponse<IBuildUpStatus[]>} SWRResponse of all available build up players' statuses
 */
export const useBuildUpStatuses = (): SWRResponse<IBuildUpStatus[]> => {
  const mockableFetcher = useMockableFetcher<IBuildUpStatus[]>((r: any) =>
    delay(() => {
      const buildUpStatuses = mockPlayerDataStore.getBuildUpStatuses();
      console.log("Mocking", r, buildUpStatuses);

      if (Math.random() > successProbability) {
        throw new Error(`Get build up statuses call failed`);
      }

      return buildUpStatuses;
    })
  );

  return useSWR<IBuildUpStatus[]>(`${ApiService.v1Url}/host/players/antifraud/statuses`, mockableFetcher);
};

/**
 * Get build up player's current status
 * @returns {SWRResponse<ICurrentBuildUpStatus>} SWRResponse of build up player's current status
 */
export const useCurrentBuildUpPlayerStatus = (playerId: number): SWRResponse<ICurrentBuildUpStatus> => {
  const mockableFetcher = useMockableFetcher<ICurrentBuildUpStatus>((r: any) =>
    delay(() => {
      const status = mockPlayerDataStore.getRecentBuildUpStatus(playerId);
      console.log("Mocking", r, status);

      if (Math.random() > successProbability) {
        throw new Error(`Get build up player's current status call failed`);
      }

      return status;
    })
  );

  return useSWR<ICurrentBuildUpStatus>(
    `${ApiService.v1Url}/host/players/antifraud/${playerId}/status`,
    mockableFetcher
  );
};

/**
 * Build up status updater
 * @returns {SWRResponse<IBuildUpStatusUpdate>} SWRResponse of build up player's player most recent status update
 */
export const useBuildUpStatusUpdater = (): {
  updateStatus: (playerId: number, statusId: number) => Promise<Response>;
} => {
  const mockableUpdate = useMockableRawUpdater<IBuildUpStatusUpdate>((_, req, body) =>
    delay(() => {
      if (Math.random() > successProbability) {
        return Promise.reject(new Response(`Build up status update call failed`, { status: 500 }));
      }

      const updatedStatus = mockPlayerDataStore.updateStatus(body);

      return Promise.resolve(new Response(JSON.stringify(updatedStatus), { status: 200 }));
    })
  );

  return {
    updateStatus: (playerId: number, newStatusId: number): Promise<Response> => {
      return mockableUpdate("POST", `${ApiService.v1Url}/host/players/antifraud/status`, {
        playerId,
        newStatusId,
      });
    },
  };
};

/**
 * Get player's free play stats
 * @returns {SWRResponse<IFreePlayStats>} SWRResponse of player's free play stats
 */
export const useFreePlayStats = (playerId: number): SWRResponse<IFreePlayStats> => {
  const mockableFetcher = useMockableFetcher<IFreePlayStats>((r: any) =>
    delay(() => {
      const stats = mockPlayerDataStore.getFreePlayStatus(playerId);
      console.log("Mocking", r, stats);

      if (Math.random() > successProbability) {
        throw new Error(`Get player's free play stats call failed`);
      }

      return stats;
    })
  );

  return useSWR<IFreePlayStats>(
    `${ApiService.v1Url}/host/players/antifraud/${playerId}/freeplayoffer`,
    mockableFetcher
  );
};

export const usePermissions = () => {
  return useSWR<string[]>(`${ApiService.v1Url}/employee/permissions?applicationKey=patron360`);
};

export const usePlayerCustomerValueScoreStats = (playerId: string) => {
  if (!playerId) throw Error("Player ID expected");
  const mockableFetcher = useMockableFetcher<IPlayerCustomerValueScore>((r: any) => {
    const customerValueScoreStats = mockPlayerDataStore.getCustomerValueScore(playerId);
    return Promise.resolve(customerValueScoreStats);
  });
  return useSWR(`${ApiService.v1Url}/players/${playerId}/stats/cvs`, mockableFetcher);
};

export const usePlayerReservationsByStatus = (
  playerId: string | number,
  status: "current" | "upcoming" | "cancelled" | "previous"
) => {
  if (!playerId) throw Error("Player ID expected");

  const mockableFetcher = useMockableFetcher<IBasicReservation[]>((r: any) => {
    const resByStatus = mockReservations.getByStatus(status);
    return Promise.resolve(resByStatus);
  });

  return useSWR<IBasicReservation[]>(
    `${ApiService.v1Url}/players/${playerId}/reservations/status/${status}`,
    mockableFetcher
  );
};

export const usePlayerImage = (playerId: string) => {
  const fetcher = useRawFetcher();
  return useSWR(`${ApiService.v1Url}/players/${playerId}/image`, fetcher);
};

/**
 * Player's Offers
 * @param {string | number} playerId - ID of the player
 * @param {number[]} categoryIds - Array of categories
 * @param {string[]} statuses - Array of statuses
 * @returns {SWRResponse<IPlayerOffer[]>}  SWR Response of a list of player offers
 */
export const usePlayerOffers = (
  playerId: string | number,
  statuses: string[],
  categoryIds: number[]
): SWRResponse<IPlayerOffer[]> => {
  const mockableFetcher = useMockableFetcher<IPlayerOffer[]>((r: any) => {
    const mockOffers = mockPlayerDataStore.offers;
    return Promise.resolve(mockOffers);
  });

  const url = new URL(`${ApiService.v1Url}/players/${playerId}/offers`);
  const params = new URLSearchParams();
  categoryIds.forEach((categoryId) => {
    console.log(categoryId);
    params.append("categoryIds", categoryId.toString());
  });
  statuses.forEach((status) => {
    params.append("statuses", status);
  });

  url.search = params.toString();
  return useSWR<IPlayerOffer[]>(url.href, mockableFetcher);
};

/**
 * Offer Categories
 * @returns {SWRResponse<IOfferCategory[]>} SWR Response of a list of offer categories
 */
export const useOfferCategories = (): SWRResponse<IOfferCategory[]> => {
  const mockableFetcher = useMockableFetcher<IOfferCategory[]>((r: any) => {
    const mockOfferCategories = [
      {
        id: 0,
        name: "Slots",
      },
      {
        id: 2,
        name: "Dining",
      },
    ];
    return Promise.resolve(mockOfferCategories);
  });
  return useSWR<IOfferCategory[]>(`${ApiService.v1Url}/offers/categories`, mockableFetcher);
};

/**
 * Basic Information about a player.
 * @param {string | number} playerId - ID of patron
 * @return {SWRResponse<IBasicPlayerInformation>} SWRResponse with basic player information
 */
export const useBasicPlayerInformation = (playerId: string): SWRResponse<IBasicPlayerInformation> => {
  const mockableFetcher = useMockableFetcher<IBasicPlayerInformation>((r: any) => {
    const basicPlayerInformation = mockPlayerDataStore.getBasicPlayerInformation(playerId);
    return Promise.resolve(basicPlayerInformation);
  });
  return useSWR(`${ApiService.v1Url}/players/${playerId}/basicinfo`, mockableFetcher);
};

/**
 * All languages
 * @returns {SWRResponse<IPlayerLanguage[]>}
 * - SWRResponse with array of all available languages from KonamiSDK
 */
export const useAllLanguages = (): SWRResponse<IPlayerLanguage[]> => {
  const mockableFetcher = useMockableFetcher<IPlayerLanguage[]>((r: any) =>
    delay(() => {
      const allLanguages = mockPlayerDataStore.getAllLanguages();
      console.log("Mocking", r, allLanguages);

      if (Math.random() > successProbability) {
        return Promise.reject(new Error(`Get all languages call failed`));
      }
      return allLanguages;
    })
  );
  return useSWR<IPlayerLanguage[]>(`${ApiService.v1Url}/player/languages`, mockableFetcher);
};

/**
 * Updates player's language preference
 * @returns {update: (playerId: string, newLanguageId: string) => Promise<Response>}
 * - Object that contains update method that takes in playerId and newLanguageId
 * as params and returns a promise of a response of type IPlayerInformation
 */
export const useLanguageUpdater = () => {
  const mockableUpdate = useMockableRawUpdater<IUpdatePlayerLanguage>((_, req, body) => {
    if (Math.random() > successProbability) {
      return Promise.reject(new Error(`Player language update call failed`));
    }

    const playerId = parseInt((req as string).split("/").reverse()[0]);
    const updated = mockPlayerDataStore.updatePlayerLanguage(playerId.toString(), body as IUpdatePlayerLanguage);
    if (updated) {
      return Promise.resolve(new Response(JSON.stringify(updated), { status: 200 }));
    } else {
      return Promise.reject(new Error(`Error updating player ${playerId} language`));
    }
  });

  return {
    update: (playerId: string, newLanguageId: string): Promise<Response> => {
      return mockableUpdate("PUT", `${ApiService.v1Url}/players/${playerId}/language`, {
        languageId: parseInt(newLanguageId),
      });
    },
  };
};

/**
 * Player information
 * @params playerId - player's ID
 * @returns {SWRResponse<IPlayerInformation>}
 * - SWRResponse with object containing player's information
 */
export const useGetPlayerInformation = (playerId: string | number) => {
  const mockableFetcher = useMockableFetcher<IPlayerInformation>((r: any) =>
    delay(() => {
      const playerInfo = mockPlayerDataStore.getPlayerInformation(playerId as string);
      console.log("Mocking", r, playerInfo);

      if (Math.random() > successProbability) {
        throw new Error(`Get player info call failed`);
      }

      return playerInfo;
    })
  );

  return useSWR<IPlayerInformation>(playerId ? `${ApiService.v1Url}/players/${playerId}` : null, mockableFetcher);
};

/**
 * Source of funds updater
 * @returns {update: (playerId: string, sourceOfFunds: IUpdateSourceOfFundsInfo) => Promise<Response>}
 * - Object that contains update method that takes in playerId and sourceOfFunds object
 * as params and returns a promise of an api response
 */
export const useFundsUpdater = () => {
  const mockableUpdate = useMockableRawUpdater<IUpdateSourceOfFundsInfo>((_, req, body) => {
    if (Math.random() > successProbability) {
      return Promise.reject(new Error(`Player's source of fund update call failed`));
    }

    return Promise.resolve(new Response());
  });

  return {
    update: (playerId: string, sourceOfFunds: IUpdateSourceOfFundsInfo): Promise<Response> => {
      const { jobTitle, company } = sourceOfFunds;
      return mockableUpdate("PUT", `${ApiService.v1Url}/players/${playerId}/employment`, {
        jobTitle,
        company,
      });
    },
  };
};

/**
 * Mobile App Updater
 * @returns {
 * unlinkUser: (playerId: string | number): Promise<Response>
 * resetPassword: (playerId: string | number): Promise<Response>
 * updateEmail: (playerId: string | number, newEmail: string): Promise<Response>
 * }
 * - Object that contains unlinkUser, resetPassword, and updateEmail method and returns a promise of an api response
 */
export const useMobileAppUpdater = () => {
  const mockableUnlink = useMockableRawUpdater<undefined>((_, req, body) => {
    if (Math.random() > successProbability) {
      return Promise.reject(new Error(`Unlink user call failed`));
    }

    return Promise.resolve(new Response());
  });

  const mockableReset = useMockableRawUpdater<undefined>((_, req, body) => {
    if (Math.random() > successProbability) {
      return Promise.reject(new Error(`Reset password call failed`));
    }

    return Promise.resolve(new Response());
  });

  const mockableUpdate = useMockableRawUpdater<{ newEmailAddress: string }>((_, req, body) => {
    if (Math.random() > successProbability) {
      return Promise.reject(new Error(`Reset password call failed`));
    }

    return Promise.resolve(new Response());
  });

  return {
    unlinkUser: (playerId: string | number): Promise<Response> =>
      mockableUnlink("DELETE", `${ApiService.v1Url}/players/${playerId}/club-serrano-online/unlink`, undefined),
    resetPassword: (playerId: string | number): Promise<Response> =>
      mockableReset("POST", `${ApiService.v1Url}/players/${playerId}/club-serrano-online/reset-password`, undefined),
    updateEmail: (playerId: string | number, newEmail: string): Promise<Response> => {
      return mockableUpdate(
        "PUT",
        `${ApiService.v1Url}/players/${playerId}/club-serrano-online/email`,
        { newEmailAddress: newEmail },
        {
          headers: { "Content-Type": "application/json" },
        }
      );
    },
  };
};

export const usePlayerStats = (playerId: string, period: string): SWRResponse<IPlayerStatsForTimePeriod> => {
  const mockableFetcher = useMockableFetcher<IPlayerStatsForTimePeriod>(async (r) => {
    return mockPeriods[period] ?? mockPeriods["7"];
  });
  const url = new URL(`${ApiService.v1Url}/players/${playerId}/stats`);
  url.search = new URLSearchParams({ period }).toString();
  return useSWR<IPlayerStatsForTimePeriod>(url.toString(), mockableFetcher);
};

export const usePlayerTripHistory = (playerId: string, period: string): SWRResponse<IPlayerTripHistory[]> => {
  const mockableFetcher = useMockableFetcher<IPlayerTripHistory[]>(async (r) => {
    return mockTripsForPeriods[period] ?? mockTripsForPeriods["7"];
  });
  const url = new URL(`${ApiService.v1Url}/players/${playerId}/stats/trips/graph`);
  url.search = new URLSearchParams({ period }).toString();
  return useSWR<IPlayerTripHistory[]>(url.toString(), mockableFetcher);
};

/**
 * Net Revenue Analysis
 * @params playerId - player's ID
 * @returns {SWRResponse<INetRevenueAnalysis>}
 * - SWRResponse of net revenue based on period
 */
export const useNetRevenue = (playerId: string, period: string): SWRResponse<INetRevenueAnalysis> => {
  const mockableFetcher = useMockableFetcher<INetRevenueAnalysis>((r: any) =>
    delay(() => {
      const result = mockPlayerDataStore.getNetRevenue(playerId, period);

      if (Math.random() > successProbability) {
        return Promise.reject(new Error(`Net revenue call failed`));
      }
      return result;
    })
  );
  return useSWR<INetRevenueAnalysis>(
    `${ApiService.v1Url}/players/${playerId}/revenue/byperiod?period=${period}`,
    mockableFetcher
  );
};

export const useTimePeriods = (): SWRResponse<ITimePeriod[]> => {
  return useSWR(`${ApiService.v1Url}/settings/periods`);
};
